import {useCallback, useEffect, useState} from 'react';
import {useIsMount} from '../useIsMount';
import {useFunctionWrap} from './useFunctionWrap';

export interface UseFirestoreLoadOptions {
  /** When true, the load function is not run and refetch is a no-op. */
  skip?: boolean;
  /** When true, the load function is not run on mount. Refetch will trigger a load*/
  skipLoadOnMount?: boolean;
}

export interface UseFirestoreLoadResult<T, A extends unknown[] = []> {
  /** data is the result of the function */
  data: T | undefined;
  /** loading is true when the function is running */
  loading: boolean;
  /** error is the error of the function */
  error: Error | undefined;
  /** refetch re-runs the load function with the same arguments as load (same signature) */
  refetch: (...args: A) => Promise<void>;
}

/**
 * Runs an async function, manages loading/error state, and exposes refetch.
 * Return of function goes to data. refetch has the same signature as load so
 * arguments are passed through with correct typing. When load requires args,
 * use skipLoadOnMount and call refetch(...) with the args.
 */
export function useFunctionRun<T, A extends unknown[] = []>(
  load: (...args: A) => Promise<T>,
  options: UseFirestoreLoadOptions = {},
): UseFirestoreLoadResult<T, A> {
  const {skip = false, skipLoadOnMount = false} = options;
  const [data, setData] = useState<T | undefined>(undefined);
  const {fn: wrappedFn, loading: wrapLoading, error: wrapError} = useFunctionWrap(load);

  const shouldRunOnMount = !skip && !skipLoadOnMount;
  const isMounted = useIsMount();

  useEffect(() => {
    if (skip || skipLoadOnMount) {
      setData(undefined);
      return;
    }
    void wrappedFn(...([] as unknown as A)).then((result) => {
      if (isMounted()) setData(result);
    });
  }, [skip, skipLoadOnMount, wrappedFn, isMounted]);

  const refetch = useCallback(
    (...args: A) => {
      if (skip) return Promise.resolve();
      return wrappedFn(...args).then(
        (result) => {
          if (isMounted()) setData(result);
        },
        (err) => {
          if (isMounted()) setData(undefined);
          throw err;
        },
      );
    },
    [skip, wrappedFn, isMounted],
  );

  const loading = skip ? false : (shouldRunOnMount && data === undefined && wrapError === undefined) || wrapLoading;

  return {
    data,
    loading,
    error: skip ? undefined : wrapError,
    refetch,
  };
}
